import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiX, FiImage, FiPaperclip } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import TaskModal from '../../components/TaskModal';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../services/api';
import '../../styles/trello-board.css';

const AdminTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({
    todo: {
      id: 'todo',
      title: 'To Do',
      taskIds: []
    },
    in_progress: {
      id: 'in_progress',
      title: 'In Progress',
      taskIds: []
    },
    testing: {
      id: 'testing',
      title: 'Testing',
      taskIds: []
    },
    client_review: {
      id: 'client_review',
      title: 'Client Review',
      taskIds: []
    },

  });
  const [columnOrder, setColumnOrder] = useState(['todo', 'in_progress', 'testing', 'client_review']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [viewMode, setViewMode] = useState('monday'); // 'monday', 'kanban' or 'project'
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskComments, setTaskComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingComment, setUploadingComment] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [assigningUser, setAssigningUser] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchClients();
    fetchProjects();
  }, []);

  const fetchTasks = async (projectId = '') => {
    try {
      setLoading(true);
      const url = projectId ? `/admin/tasks?project_id=${projectId}` : '/admin/tasks';
      const response = await api.get(url);
      const fetchedTasks = response.data;
      
      // Organize tasks into columns
      const todoTasks = [];
      const inProgressTasks = [];
      const testingTasks = [];
      const clientReviewTasks = [];
      
      fetchedTasks.forEach(task => {
        switch(task.status) {
          case 'todo':
            todoTasks.push(task.id);
            break;
          case 'in_progress':
            inProgressTasks.push(task.id);
            break;
          case 'testing':
            testingTasks.push(task.id);
            break;
          case 'client_review':
            clientReviewTasks.push(task.id);
            break;
          default:
            // Don't add completed tasks or unknown status tasks
            if (task.status !== 'completed') {
              todoTasks.push(task.id);
            }
        }
      });
      
      setTasks(fetchedTasks.reduce((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}));
      
      setColumns({
        todo: {
          id: 'todo',
          title: 'To Do',
          taskIds: todoTasks
        },
        in_progress: {
          id: 'in_progress',
          title: 'In Progress',
          taskIds: inProgressTasks
        },
        testing: {
          id: 'testing',
          title: 'Testing',
          taskIds: testingTasks
        },
        client_review: {
          id: 'client_review',
          title: 'Client Review',
          taskIds: clientReviewTasks
        }
      });
      
      console.log('Fetched tasks:', fetchedTasks.length);
      console.log('Organized tasks object keys:', Object.keys(tasks));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', {
        params: { limit: 1000 } // Get enough users for task assignment
      });
      setUsers(response.data.users || response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data.projects || response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const handleCreateTask = () => {
    if (selectedProject) {
      const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));
      const prefilledTask = {
        project_id: selectedProjectData?.id,
        client_id: selectedProjectData?.client_id || null
      };
      setCurrentTask(prefilledTask);
    } else {
      setCurrentTask(null);
    }
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setShowTaskModal(true);
  };

  const handleDeleteTask = (task) => {
    setCurrentTask(task);
    setShowDeleteModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      console.log('Saving task with data:', taskData);
      console.log('Current task (for edit check):', currentTask);
      
      if (currentTask && currentTask.id) {
        // Update existing task (has ID)
        const response = await api.put(`/admin/tasks/${currentTask.id}`, taskData);
        console.log('Task updated successfully:', response.data);
      } else {
        // Create new task (no ID or prefilled data)
        const response = await api.post('/admin/tasks', taskData);
        console.log('Task created successfully:', response.data);
      }
      setShowTaskModal(false);
      // Refresh tasks to get the updated list
      await fetchTasks(selectedProject);
    } catch (err) {
      console.error('Error saving task:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
      }
      alert('Failed to save task: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    // If there's no destination or the item was dropped back in the same place
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }
    
    // Get the task that was dragged
    const taskId = draggableId;
    const task = tasks[taskId];
    
    // Check if task exists
    if (!task) {
      console.error('Task not found in local state:', taskId);
      console.error('Available tasks:', Object.keys(tasks));
      console.error('Trying to refresh tasks and retry...');
      await fetchTasks(selectedProject);
      return;
    }
    
    // If the status has changed, update it in the backend
    if (destination.droppableId !== source.droppableId) {
      try {
        // Update the task status in the backend
        await api.put(`/admin/tasks/${taskId}`, {
          status: destination.droppableId
        });
        
        // Update local state
        const newTasks = {
          ...tasks,
          [taskId]: {
            ...task,
            status: destination.droppableId
          }
        };
        setTasks(newTasks);
      } catch (err) {
        console.error('Error updating task status:', err);
        console.error('Error details:', err.response?.data);
        alert('Failed to update task status: ' + (err.response?.data?.message || err.message));
        // Refresh tasks to get current state
        fetchTasks(selectedProject);
        return;
      }
    }
    
    // Update the columns state
    const startColumn = columns[source.droppableId];
    const endColumn = columns[destination.droppableId];
    
    if (startColumn === endColumn) {
      // Reordering within the same column
      const newTaskIds = Array.from(startColumn.taskIds);
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, taskId);
      
      const newColumn = {
        ...startColumn,
        taskIds: newTaskIds
      };
      
      setColumns({
        ...columns,
        [newColumn.id]: newColumn
      });
    } else {
      // Moving from one column to another
      const startTaskIds = Array.from(startColumn.taskIds);
      startTaskIds.splice(source.index, 1);
      
      const newStartColumn = {
        ...startColumn,
        taskIds: startTaskIds
      };
      
      const endTaskIds = Array.from(endColumn.taskIds);
      endTaskIds.splice(destination.index, 0, taskId);
      
      const newEndColumn = {
        ...endColumn,
        taskIds: endTaskIds
      };
      
      setColumns({
        ...columns,
        [newStartColumn.id]: newStartColumn,
        [newEndColumn.id]: newEndColumn
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/admin/tasks/${currentTask.id}`);
      setShowDeleteModal(false);
      fetchTasks(selectedProject);
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleTaskClick = async (task) => {
    console.log('Selected task:', task);
    console.log('Task Users:', task.Users);
    setSelectedTask(task);
    await fetchTaskComments(task.id);
    await fetchAvailableUsers();
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/users');
      setAvailableUsers(response.data.users || response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAssignUser = async (userId) => {
    try {
      setAssigningUser(true);
      console.log('Assigning user:', userId, 'to task:', selectedTask.id);
      
      const currentAssignedIds = selectedTask.Users?.map(u => u.id) || [];
      const newAssignedIds = [...currentAssignedIds, parseInt(userId)];
      
      console.log('Current assigned IDs:', currentAssignedIds);
      console.log('New assigned IDs:', newAssignedIds);
      
      const response = await api.put(`/admin/tasks/${selectedTask.id}`, {
        assigned_users: newAssignedIds
      });
      
      console.log('Assignment response:', response.data);
      
      // Update selected task with new user
      const newUser = availableUsers.find(u => u.id === parseInt(userId));
      if (newUser) {
        const updatedTask = {
          ...selectedTask,
          Users: [...(selectedTask.Users || []), newUser]
        };
        setSelectedTask(updatedTask);
        console.log('Updated task:', updatedTask);
      }
    } catch (error) {
      console.error('Error assigning user:', error);
      alert('Failed to assign user: ' + error.message);
    } finally {
      setAssigningUser(false);
    }
  };

  const handleUnassignUser = async (userId) => {
    try {
      console.log('Unassigning user:', userId, 'from task:', selectedTask.id);
      
      const currentAssignedIds = selectedTask.Users?.map(u => u.id) || [];
      const newAssignedIds = currentAssignedIds.filter(id => id !== userId);
      
      console.log('Current assigned IDs:', currentAssignedIds);
      console.log('New assigned IDs after removal:', newAssignedIds);
      
      const response = await api.put(`/admin/tasks/${selectedTask.id}`, {
        assigned_users: newAssignedIds
      });
      
      console.log('Unassignment response:', response.data);
      
      // Update selected task
      const updatedTask = {
        ...selectedTask,
        Users: selectedTask.Users?.filter(u => u.id !== userId) || []
      };
      setSelectedTask(updatedTask);
      console.log('Updated task after removal:', updatedTask);
    } catch (error) {
      console.error('Error unassigning user:', error);
      alert('Failed to unassign user: ' + error.message);
    }
  };

  const fetchTaskComments = async (taskId) => {
    try {
      setLoadingComments(true);
      const response = await api.get(`/admin/tasks/${taskId}/comments`);
      setTaskComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && !selectedImage) return;
    
    try {
      setUploadingComment(true);
      const response = await api.post(`/admin/tasks/${selectedTask.id}/comments`, {
        content: newComment
      });
      
      // Add the new comment to the existing comments immediately
      setTaskComments([...taskComments, response.data]);
      setNewComment('');
      setSelectedImage(null);
      
      // Auto scroll to bottom after adding comment
      setTimeout(() => {
        const commentsContainer = document.querySelector('.comments-scroll-container');
        if (commentsContainer) {
          commentsContainer.scrollTop = commentsContainer.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setUploadingComment(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
    } else {
      alert('Please select a valid image file');
    }
  };

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    try {
      await api.put(`/admin/tasks/${taskId}`, { status: newStatus });
      setSelectedTask({...selectedTask, status: newStatus});
      // Update local state without page reload
      const updatedTasks = Object.values(tasks).map(t => 
        t.id === taskId ? {...t, status: newStatus} : t
      );
      const tasksObj = {};
      updatedTasks.forEach(t => tasksObj[t.id] = t);
      setTasks(tasksObj);
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'testing': return 'bg-purple-500';
      case 'client_review': return 'bg-orange-500';
      case 'todo': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Tasks</h1>
        <div className="text-center py-8">Loading tasks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Tasks</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchTasks} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tasks</h1>
        <div className="flex items-center space-x-4">
          <div className="flex bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('monday')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'monday' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              Table View
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'kanban' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              Kanban
            </button>
            <button
              onClick={() => setViewMode('project')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'project' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              Project View
            </button>
          </div>
          <select
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              fetchTasks(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleCreateTask}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
          >
            <FiPlus className="mr-2" /> Create Task
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">Loading tasks...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchTasks}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      ) : Object.keys(tasks).length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 mb-4">No tasks found</p>
          <button
            onClick={handleCreateTask}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Create Your First Task
          </button>
        </div>
      ) : viewMode === 'monday' ? (
        /* Monday.com Style View */
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 w-80">Task</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Status</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Priority</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-48">Assigned To</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Due Date</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-40">Project</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.values(tasks).map((task, index) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`}></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleTaskClick(task)}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors text-left"
                          >
                            {task.title}
                          </button>
                          {task.description && (
                            <p className="text-xs text-gray-500 mt-1 truncate max-w-xs" title={task.description.replace(/<[^>]*>/g, ' ').trim()}>
                              {task.description.replace(/<[^>]*>/g, ' ').trim().substring(0, 50)}{task.description.replace(/<[^>]*>/g, ' ').trim().length > 50 ? '...' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <select
                        value={task.status}
                        onChange={async (e) => {
                          try {
                            await api.put(`/admin/tasks/${task.id}`, { status: e.target.value });
                            fetchTasks(selectedProject);
                          } catch (error) {
                            console.error('Error updating task status:', error);
                            alert('Failed to update task status');
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(task.status)} text-white`}
                      >
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="testing">Testing</option>
                        <option value="client_review">Client Review</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)} text-white`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center space-x-1">
                        {task.Users && task.Users.length > 0 ? (
                          task.Users.slice(0, 3).map((user, idx) => (
                            <div key={user.id} className="flex-shrink-0">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Unassigned</span>
                        )}
                        {task.Users && task.Users.length > 3 && (
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs">
                            +{task.Users.length - 3}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-sm text-gray-600">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                        {task.Project?.name || 'No Project'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button onClick={() => handleTaskClick(task)} className="text-blue-500 hover:text-blue-700 transition-colors">
                          <FiEye size={16} />
                        </button>
                        <button onClick={() => handleEditTask(task)} className="text-gray-500 hover:text-gray-700 transition-colors">
                          <FiEdit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteTask(task)} className="text-red-500 hover:text-red-700 transition-colors">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : viewMode === 'kanban' ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="board-container">
            {columnOrder.map(columnId => {
              const column = columns[columnId];
              const columnTasks = column.taskIds.map(taskId => tasks[taskId]).filter(Boolean);
              
              return (
                <div key={column.id} className="board-column">
                  <h2 className="board-column-title">{column.title}</h2>
                  
                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`task-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                      >
                        {columnTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`task-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              >
                                <div className="task-card-header">
                                  <h3 className="task-card-title">{task.title}</h3>
                                  <div className="task-card-actions">
                                    <Link
                                      to={`/admin/tasks/${task.id}`}
                                      className="text-blue-600 hover:text-blue-900 p-1"
                                    >
                                      <FiEye size={14} />
                                    </Link>
                                    <button
                                      onClick={() => handleEditTask(task)}
                                      className="text-indigo-600 hover:text-indigo-900 p-1"
                                    >
                                      <FiEdit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTask(task)}
                                      className="text-red-600 hover:text-red-900 p-1"
                                    >
                                      <FiTrash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                                
                                {task.description && (
                                  <div className="text-xs text-gray-600 mb-2 line-clamp-2">
                                    {task.description.replace(/<[^>]*>/g, ' ').trim()}
                                  </div>
                                )}
                                
                                <div className="task-card-footer">
                                  <span className={`task-priority-badge priority-${task.priority}`}>
                                    {task.priority}
                                  </span>
                                  <span className="task-due-date">
                                    {formatDate(task.due_date)}
                                  </span>
                                </div>
                                
                                {task.Client && (
                                  <div className="task-meta">
                                    Client: {task.Client.name}
                                  </div>
                                )}
                                
                                {task.Project && (
                                  <div className="task-meta">
                                    Project: {task.Project.name}
                                  </div>
                                )}
                                
                                {task.Users && task.Users.length > 0 && (
                                  <div className="task-meta">
                                    Assigned: {task.Users.map(user => user.name).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      ) : (
        /* Project View */
        <div className="space-y-6">
          {Object.entries(
            Object.values(tasks).reduce((groups, task) => {
              const projectName = task.Project?.name || 'No Project';
              if (!groups[projectName]) groups[projectName] = [];
              groups[projectName].push(task);
              return groups;
            }, {})
          ).map(([projectName, projectTasks]) => (
            <div key={projectName} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">{projectName}</h3>
                <p className="text-sm text-gray-500">{projectTasks.length} tasks</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projectTasks.map((task) => (
                      <tr key={task.id}>
                        <td className="px-6 py-4">
                          <Link 
                            to={`/admin/tasks/${task.id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                          >
                            {task.title}
                          </Link>
                          {task.description && (
                            <div className="text-xs text-gray-500 mt-1 truncate max-w-xs" title={task.description.replace(/<[^>]*>/g, ' ').trim()}>
                              {task.description.replace(/<[^>]*>/g, ' ').trim()}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full text-white ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full text-white ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {task.Users && task.Users.length > 0 ? task.Users.map(user => user.name).join(', ') : 'Unassigned'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(task.due_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <Link to={`/admin/tasks/${task.id}`} className="text-blue-600 hover:text-blue-900" title="View Task">
                              <FiEye />
                            </Link>
                            <button onClick={() => handleEditTask(task)} className="text-indigo-600 hover:text-indigo-900" title="Edit Task">
                              <FiEdit2 />
                            </button>
                            <button onClick={() => handleDeleteTask(task)} className="text-red-600 hover:text-red-900" title="Delete Task">
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {showTaskModal && (
        <TaskModal
          task={currentTask}
          onClose={() => setShowTaskModal(false)}
          onSave={handleSaveTask}
          users={users}
          clients={clients}
          projects={projects}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Task"
          message={`Are you sure you want to delete "${currentTask.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Task Details Sidebar */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setSelectedTask(null)}></div>
          <div className="absolute right-0 top-0 h-full w-full md:w-[500px] bg-white shadow-xl">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">{selectedTask.title}</h2>
                  <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600">
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Task Details */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-sm text-gray-600">{selectedTask.description ? selectedTask.description.replace(/<[^>]*>/g, ' ').trim() : 'No description'}</p>
                </div>

                {/* Status Update */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Status</h3>
                  <select
                    value={selectedTask.status}
                    onChange={(e) => handleTaskStatusUpdate(selectedTask.id, e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="testing">Testing</option>
                    <option value="client_review">Client Review</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Task Info */}
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Priority: </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getPriorityColor(selectedTask.priority)}`}>
                      {selectedTask.priority}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Due Date: </span>
                    <span className="text-sm text-gray-600">{formatDate(selectedTask.due_date)}</span>
                  </div>
                  {selectedTask.Project && (
                    <div>
                      <span className="text-sm font-medium text-gray-900">Project: </span>
                      <span className="text-sm text-gray-600">{selectedTask.Project.name}</span>
                    </div>
                  )}
                  {selectedTask.Client && (
                    <div>
                      <span className="text-sm font-medium text-gray-900">Client: </span>
                      <span className="text-sm text-gray-600">{selectedTask.Client.name}</span>
                    </div>
                  )}
                </div>

                {/* Assigned Developers */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Assigned Developers</h3>
                  
                  {/* Current Assignments */}
                  <div className="mb-4">
                    <div className="min-h-[40px] border border-gray-200 rounded-md p-2 bg-gray-50">
                      {console.log('Rendering assigned users:', selectedTask.Users)}
                      {selectedTask.Users && selectedTask.Users.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedTask.Users.map((user) => (
                            <div key={user.id} className="flex items-center bg-blue-500 text-white px-3 py-1 rounded-full text-sm">
                              <span className="mr-2">{user.name || 'Unknown User'}</span>
                              <button
                                onClick={() => {
                                  console.log('Removing user:', user.id, user.name);
                                  handleUnassignUser(user.id);
                                }}
                                className="text-white hover:text-gray-200 ml-1 flex items-center justify-center"
                                type="button"
                              >
                                <FiX size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No developers assigned (Users: {JSON.stringify(selectedTask.Users)})</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Add Developer with Search */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Add Developer</label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignUser(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      disabled={assigningUser}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a developer...</option>
                      {availableUsers
                        .filter(user => !selectedTask.Users?.some(u => u.id === user.id))
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email || 'No email'})
                          </option>
                        ))
                      }
                    </select>
                    {assigningUser && (
                      <p className="text-xs text-gray-500 mt-1">Assigning developer...</p>
                    )}
                  </div>
                </div>

                {/* Comments Section */}
                <div className="flex flex-col h-96">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Comments</h3>
                  
                  {/* Comments List - Scrollable */}
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2 comments-scroll-container">
                    {loadingComments ? (
                      <p className="text-sm text-gray-500">Loading comments...</p>
                    ) : taskComments.length > 0 ? (
                      taskComments.map((comment) => (
                        <div key={comment.id} className="bg-gray-50 rounded-md p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{comment.User?.name || 'Unknown'}</span>
                            <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                          </div>
                          {comment.content && (
                            <p className="text-sm text-gray-600 mb-2" style={{whiteSpace: 'pre-wrap'}}>{comment.content}</p>
                          )}
                          {comment.image_url && (
                            <div className="mt-2">
                              <img
                                src={`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${comment.image_url}`}
                                alt="Comment attachment"
                                className="max-w-full h-auto rounded-md border border-gray-200"
                                style={{ maxHeight: '200px' }}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No comments yet</p>
                    )}
                  </div>

                  {/* Add Comment - Fixed at Bottom */}
                  <div className="border-t pt-3">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows="3"
                    />
                    
                    {/* Bottom Row - Image Upload and Add Button */}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50"
                        >
                          <FiImage className="mr-1" size={14} />
                          Attach Image
                        </label>
                        
                        {selectedImage && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600">{selectedImage.name}</span>
                            <button
                              onClick={() => setSelectedImage(null)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={handleAddComment}
                        disabled={(!newComment.trim() && !selectedImage) || uploadingComment}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 flex items-center"
                      >
                        {uploadingComment ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                            Adding...
                          </>
                        ) : (
                          'Add Comment'
                        )}
                      </button>
                    </div>
                  </div>


                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTasksPage;